import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from 'primereact/button'
import { Messages } from 'primereact/messages'

import { useBackgroundTaskStatus } from '../../../../../../data/batchstatus/hooks'
import { BackgroundTaskPhase } from '../../../../../../data/batchstatus/models'
import { Progressbar } from '../../../../backgroundTaskStatus/dialog/Progressbar'
import { useAllSuppliers } from '../../../data'

type Props = {
  visible: boolean
  location: string
  retry?: string
  isCreate: boolean
  isDelete: boolean
  isSupplierProcess: boolean
  onExit: () => void
  isLoading: boolean
  setFooter: (content: React.ReactNode) => void
}

export const SummaryPanel: React.FC<Props> = ({
  isCreate,
  location,
  retry,
  isDelete,
  visible,
  isSupplierProcess,
  onExit,
  isLoading = false,
  setFooter,
}) => {
  const { mutate: mutateAllSuppliers } = useAllSuppliers()
  const { data: backgroundStatus } = useBackgroundTaskStatus(location, retry)
  const { t } = useTranslation()

  const msgs = useRef<Messages>(null)

  // Errors
  useEffect(() => {
    if (backgroundStatus?.errors) {
      msgs.current?.clear()
      backgroundStatus.errors.forEach(error => {
        msgs.current?.show({
          sticky: true,
          severity: 'error',
          summary: t('roleAssignments.table.backroundTask.error'),
          detail: error.errorMessage,
        })
      })
    }
  }, [backgroundStatus?.errors])

  // Footer
  useEffect(() => {
    if (backgroundStatus?.phase === BackgroundTaskPhase.Completed) {
      // Update the suppliers list
      mutateAllSuppliers()

      setFooter(
        <div className="flex pt-4 justify-content-end">
          <Button
            label={t('suppliers.table.upload.dialog.summaryPanel.footer.next')}
            onClick={onExit}
            disabled={isLoading}
          />
        </div>,
      )
    } else {
      setFooter(null)
    }
  }, [backgroundStatus?.phase, isLoading])

  return (
    <>
      {backgroundStatus?.phase !== BackgroundTaskPhase.Completed ? (
        <Progressbar
          isCreate={isCreate}
          isDelete={isDelete}
          isSupplierProcess={isSupplierProcess}
          backgroundStatus={backgroundStatus}
          visible={visible}
        />
      ) : (
        <>
          <div className="flex flex-column align-items-center my-6">
            {backgroundStatus.successfullyProcessedItems.length === 0 ? (
              <span
                className="pi pi-times-circle p-text-failure"
                style={{ color: 'var(--red-500)', fontSize: '4rem' }}
              ></span>
            ) : backgroundStatus.errors.length > 0 ? (
              <span
                className="pi pi-exclamation-triangle p-text-warning"
                style={{ color: 'var(--yellow-500)', fontSize: '4rem' }}
              ></span>
            ) : (
              <span
                className="pi pi-check-circle p-text-success"
                style={{ color: 'var(--green-500)', fontSize: '4rem' }}
              ></span>
            )}

            {backgroundStatus.successfullyProcessedItems.length === 0 ? (
              <h3 className="mt-4">
                {t('suppliers.table.upload.dialog.summaryPanel.errorMessage')}
              </h3>
            ) : backgroundStatus.errors.length > 0 ? (
              <h3 className="mt-4">
                {t('suppliers.table.upload.dialog.summaryPanel.error')}
              </h3>
            ) : (
              <h3 className="mt-4">
                {t('suppliers.table.upload.dialog.summaryPanel.success')}
              </h3>
            )}

            <div className="mt-1">
              {t('suppliers.table.upload.dialog.summaryPanel.process', {
                processed: backgroundStatus.successfullyProcessedItems.length,
                total: backgroundStatus.totalItems,
              })}
            </div>
          </div>

          <Messages className="w-full" ref={msgs} />
        </>
      )}
    </>
  )
}
